import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';

// THE OVERSIGHT DESK — the state broadband office's morning screen.
//
// A grantor does not start their day by picking a subgrantee out of a dropdown; they
// start it by asking "what landed on my desk, and what is aging out from under me?"
// So this page is two zones and nothing else:
//   (a) the portfolio roll-up — who holds what, how much federal money is behind it
//   (b) the work queue — open de-scope requests, oldest first, with an aging badge
// Everything on screen comes from /descope/portfolio and /descope/reviews. There are
// no illustrative numbers here: if a figure is not in the database it renders as an
// explicit dash or an "unavailable" note, never as a plausible-looking placeholder.

type PortfolioRow = {
  award_id: string;
  state: string;
  subgrantee: string;
  project_id: string;
  project_code: string;
  project_name: string;
  awarded_bsl_count: number | null;
  grant_amount_usd: string | number | null;
  awarded_locations: string | number;
};

// The review-list row. Territory A owns the shape; every field past `id` is read
// optionally so this page still renders against a partially-built API rather than
// crashing the state's landing page over a column that has not shipped yet.
type ReviewRow = {
  id: string;
  project_id?: string | null;
  project_code?: string | null;
  subgrantee_label?: string | null;
  requested_n?: number | null;
  cost_total?: number | string | null;
  cost_per_passing?: number | string | null;
  ratio?: number | string | null;
  status?: string | null;
  decision?: string | null;
  received_at?: string | null;
  due_by?: string | null;
  days_open?: number | null;
  decided_at?: string | null;
  assigned_to?: string | null;
  assignee_name?: string | null;
  federal_at_risk_usd?: number | string | null;
};

const num = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const usd = (v: unknown, dp = 0) => {
  const n = num(v);
  return n == null
    ? '—'
    : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: dp });
};
const int = (v: unknown) => {
  const n = num(v);
  return n == null ? '—' : n.toLocaleString('en-US');
};
const shortDate = (v: unknown) => {
  if (!v) return '—';
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// A request is "released" when the decision let the subgrantee out of passings. The
// grantor's cumulative exposure is the sum of those, so the vocabulary has to be
// matched loosely — Territory A may report it in `decision` or fold it into `status`.
const isReleased = (r: ReviewRow) => /release/i.test(String(r.decision ?? r.status ?? ''));
const isOpen = (r: ReviewRow) => !r.decided_at && !/release|den|withdraw|closed/i.test(String(r.decision ?? r.status ?? 'open'));

/** Days a request has been sitting, preferring the server's own count. */
function daysOpen(r: ReviewRow): number | null {
  if (r.days_open != null) return Math.max(0, Math.round(Number(r.days_open)));
  if (!r.received_at) return null;
  const t = new Date(String(r.received_at)).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}

/** Red/amber/green against the statutory clock the request was stamped with. */
function aging(r: ReviewRow): { tone: 'danger' | 'warning' | 'success'; text: string } | null {
  if (!r.due_by) return null;
  const t = new Date(String(r.due_by)).getTime();
  if (Number.isNaN(t)) return null;
  const left = Math.floor((t - Date.now()) / 86_400_000);
  if (left < 0) return { tone: 'danger', text: `${-left}d overdue` };
  if (left <= 7) return { tone: 'warning', text: `due in ${left}d` };
  return { tone: 'success', text: `due in ${left}d` };
}

export default function GrantorQueuePage() {
  const nav = useNavigate();

  const portfolio = useQuery<{ anonymised?: boolean; subgrantees?: PortfolioRow[] }>({
    queryKey: ['descope-portfolio'],
    queryFn: () => api.get('/descope/portfolio'),
  });

  // One unfiltered fetch drives both zones: the queue is the open subset and the
  // exposure tiles are the decided subset. Two filtered calls would let the two
  // halves of the same screen disagree with each other mid-refresh.
  const reviews = useQuery<ReviewRow[]>({
    queryKey: ['descope-reviews', 'all'],
    // The record spine may not be deployed on every box yet. A 404 here must degrade
    // to "no request records", not to a red banner over the state's landing page.
    queryFn: async () => {
      const d = await api.get('/descope/reviews');
      return Array.isArray(d) ? d : (d?.reviews ?? d?.rows ?? []);
    },
    retry: false,
  });
  const reviewsMissing =
    reviews.isError && /^(404|403)/.test(String((reviews.error as any)?.message ?? ''));
  const rows: PortfolioRow[] = portfolio.data?.subgrantees ?? [];
  const revRows: ReviewRow[] = reviews.data ?? [];

  const openRows = useMemo(
    () =>
      revRows
        .filter(isOpen)
        .sort((a, b) => (daysOpen(b) ?? 0) - (daysOpen(a) ?? 0)),
    [revRows],
  );

  // Roll the project-grain portfolio up to the subgrantee grain the state actually
  // manages, and attach each subgrantee's live request counts by project id.
  const bySub = useMemo(() => {
    const projOwner = new Map<string, string>();
    const m = new Map<string, {
      subgrantee: string; states: Set<string>; projects: number;
      awardedPassings: number; grantUsd: number; open: number;
      releasedPassings: number; releasedUsd: number; releasedKnown: boolean;
    }>();
    for (const r of rows) {
      projOwner.set(r.project_id, r.subgrantee);
      const cur = m.get(r.subgrantee) ?? {
        subgrantee: r.subgrantee, states: new Set<string>(), projects: 0,
        awardedPassings: 0, grantUsd: 0, open: 0,
        releasedPassings: 0, releasedUsd: 0, releasedKnown: false,
      };
      cur.projects += 1;
      if (r.state) cur.states.add(r.state);
      cur.awardedPassings += num(r.awarded_locations) ?? 0;
      cur.grantUsd += num(r.grant_amount_usd) ?? 0;
      m.set(r.subgrantee, cur);
    }
    for (const rv of revRows) {
      const owner = rv.project_id ? projOwner.get(rv.project_id) : undefined;
      const cur = owner ? m.get(owner) : undefined;
      if (!cur) continue;
      if (isOpen(rv)) cur.open += 1;
      if (isReleased(rv)) {
        cur.releasedPassings += num(rv.requested_n) ?? 0;
        // Prefer the federal figure when the API supplies it; otherwise carry the
        // independent build cost and say so in the column header.
        const exposure = num(rv.federal_at_risk_usd) ?? num(rv.cost_total);
        if (exposure != null) { cur.releasedUsd += exposure; cur.releasedKnown = true; }
      }
    }
    return [...m.values()].sort((a, b) => b.awardedPassings - a.awardedPassings);
  }, [rows, revRows]);

  const totals = useMemo(() => ({
    subgrantees: bySub.length,
    projects: rows.length,
    awardedPassings: bySub.reduce((a, s) => a + s.awardedPassings, 0),
    grantUsd: bySub.reduce((a, s) => a + s.grantUsd, 0),
    releasedPassings: bySub.reduce((a, s) => a + s.releasedPassings, 0),
    releasedUsd: bySub.reduce((a, s) => a + s.releasedUsd, 0),
    releasedKnown: bySub.some((s) => s.releasedKnown),
  }), [bySub, rows.length]);

  const oldest = openRows.length ? daysOpen(openRows[0]) : null;

  const muted: CSSProperties = { color: 'var(--text-muted)' };
  const cellNum: CSSProperties = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  return (
    <div style={{ maxWidth: 1180 }}>
      <div className="page__header">
        <div>
          <h1 className="page__title">Oversight Queue</h1>
          <div className="page__subtitle">
            Every subgrantee this office administers, and every de-scope request waiting on a
            determination. Requests are worked oldest first — the aging badge is measured against
            the response window stamped on the request when it was received.
          </div>
        </div>
        <Link className="btn" to="/grantor/ledger">Decision Ledger →</Link>
      </div>

      {/* ---------- Portfolio tiles ---------- */}
      <div className="stat-grid" style={{ marginBottom: 18 }}>
        <div className="stat-card stat-card--primary">
          <div className="stat-card__label">Subgrantees</div>
          <div className="stat-card__value">{portfolio.isLoading ? '…' : int(totals.subgrantees)}</div>
          <div className="stat-card__delta">{int(totals.projects)} awarded project(s)</div>
        </div>
        <div className="stat-card stat-card--info">
          <div className="stat-card__label">Awarded passings</div>
          <div className="stat-card__value">{portfolio.isLoading ? '…' : int(totals.awardedPassings)}</div>
          <div className="stat-card__delta">locations under an active build obligation</div>
        </div>
        <div className="stat-card stat-card--success">
          <div className="stat-card__label">Grant awarded</div>
          <div className="stat-card__value">{portfolio.isLoading ? '…' : usd(totals.grantUsd)}</div>
          <div className="stat-card__delta">sum of project grant amounts on record</div>
        </div>
        <div className={'stat-card ' + (openRows.length ? 'stat-card--warning' : 'stat-card--success')}>
          <div className="stat-card__label">Open requests</div>
          <div className="stat-card__value">{reviewsMissing ? '—' : int(openRows.length)}</div>
          <div className="stat-card__delta">
            {reviewsMissing ? 'request records unavailable'
              : oldest == null ? 'nothing waiting'
              : `oldest waiting ${oldest} day(s)`}
          </div>
        </div>
        <div className="stat-card stat-card--danger">
          <div className="stat-card__label">Released to date</div>
          <div className="stat-card__value">{reviewsMissing ? '—' : int(totals.releasedPassings)}</div>
          <div className="stat-card__delta">
            {totals.releasedKnown
              ? `${usd(totals.releasedUsd)} independent build cost of released passings`
              : 'passings released by determination'}
          </div>
        </div>
      </div>

      {reviewsMissing && (
        <div className="map-banner map-banner--info" style={{ marginBottom: 16 }}>
          The de-scope request register is not answering on this deployment, so the queue below is
          empty and the request counters read as unavailable. The portfolio figures are live.
        </div>
      )}

      {/* ---------- Zone A: portfolio roll-up ---------- */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card__header">
          <span>Portfolio</span>
          <span style={{ ...muted, fontSize: 12, fontWeight: 400 }}>
            {portfolio.data?.anonymised
              ? 'Subgrantee names are masked on this view'
              : 'Subgrantee names revealed'}
          </span>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Subgrantee</th>
              <th>State</th>
              <th style={cellNum}>Projects</th>
              <th style={cellNum}>Awarded passings</th>
              <th style={cellNum}>Grant awarded</th>
              <th style={cellNum}>Open requests</th>
              <th style={cellNum}>Released passings</th>
            </tr>
          </thead>
          <tbody>
            {portfolio.isLoading && (
              <tr><td colSpan={7} style={muted}>Loading portfolio…</td></tr>
            )}
            {!portfolio.isLoading && !bySub.length && (
              <tr><td colSpan={7} style={muted}>No awards are loaded on this deployment yet.</td></tr>
            )}
            {bySub.map((s) => (
              <tr key={s.subgrantee}>
                <td style={{ fontWeight: 600 }}>{s.subgrantee}</td>
                <td>{[...s.states].join(', ') || '—'}</td>
                <td style={cellNum}>{int(s.projects)}</td>
                <td style={cellNum}>{int(s.awardedPassings)}</td>
                <td style={cellNum}>{usd(s.grantUsd)}</td>
                <td style={cellNum}>
                  {reviewsMissing ? '—' : s.open
                    ? <span className="pill pill--warning">{s.open}</span>
                    : <span style={muted}>0</span>}
                </td>
                <td style={cellNum}>{reviewsMissing ? '—' : int(s.releasedPassings)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ---------- Zone B: the work queue ---------- */}
      <div className="card">
        <div className="card__header">
          <span>Open de-scope requests</span>
          <span style={{ ...muted, fontSize: 12, fontWeight: 400 }}>oldest first</span>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Subgrantee</th>
              <th>Project</th>
              <th style={cellNum}>Passings requested</th>
              <th>Received</th>
              <th style={cellNum}>Days open</th>
              <th>Response due</th>
              <th>Assigned to</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {reviews.isLoading && <tr><td colSpan={8} style={muted}>Loading queue…</td></tr>}
            {!reviews.isLoading && !openRows.length && (
              <tr>
                <td colSpan={8} style={muted}>
                  Nothing waiting. A request appears here the moment a reviewer records one on the{' '}
                  <Link to="/descope">De-Scope Review</Link> screen.
                </td>
              </tr>
            )}
            {openRows.map((r) => {
              const age = aging(r);
              const d = daysOpen(r);
              return (
                <tr
                  key={r.id}
                  onClick={() => nav(`/descope?review=${encodeURIComponent(r.id)}`)}
                  style={{ cursor: 'pointer' }}
                  title="Open this request in the De-Scope Review desk"
                >
                  <td style={{ fontWeight: 600 }}>{r.subgrantee_label ?? '—'}</td>
                  <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                    {r.project_code ?? r.project_id ?? '—'}
                  </td>
                  <td style={cellNum}>{int(r.requested_n)}</td>
                  <td>{shortDate(r.received_at)}</td>
                  <td style={cellNum}>{d == null ? '—' : d}</td>
                  <td>
                    {shortDate(r.due_by)}{' '}
                    {age && <span className={'pill pill--' + age.tone} style={{ marginLeft: 6 }}>{age.text}</span>}
                  </td>
                  <td style={r.assignee_name || r.assigned_to ? undefined : muted}>
                    {r.assignee_name ?? r.assigned_to ?? 'Unassigned'}
                  </td>
                  <td><span className="pill pill--info">{r.status ?? 'open'}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p style={{ ...muted, fontSize: 12, marginTop: 14, maxWidth: 820 }}>
        Costs shown anywhere in this desk are independent engineering estimates produced by this
        office, not legal cost determinations and not the subgrantee's own accounting.
      </p>
    </div>
  );
}
